import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { UpgradeButton } from "@/components/UpgradeButton";
import { CreditPackButton } from "@/components/CreditPackButton";
import { CREDIT_PACKS, type Plan, type CreditPack } from "@/lib/billing";

interface Tier {
  name: string;
  /** Plan id used by the billing API. Free tier has no plan id. */
  plan?: Plan;
  price: string;
  pricePeriod?: string;
  description: string;
  cta: { label: string; to: string };
  highlight?: boolean;
  features: { included: boolean; label: string }[];
}

const TIERS: Tier[] = [
  {
    name: "Free",
    price: "$0",
    pricePeriod: "forever",
    description: "Try the product. No credit card.",
    cta: { label: "Get started", to: "/login" },
    features: [
      { included: true, label: "1 document / day" },
      { included: true, label: "All 4 schemas (receipt, invoice, business card, free text)" },
      { included: true, label: "JPG, PNG, PDF — up to 5 MB" },
      { included: true, label: "Web upload UI" },
      { included: true, label: "Download as JSON" },
      { included: false, label: "REST API access" },
      { included: false, label: "Document history beyond 24 hours" },
      { included: false, label: "Batch upload" },
    ],
  },
  {
    name: "Starter",
    plan: "starter",
    price: "$9",
    pricePeriod: "/ month",
    description: "For weekly use. Coffee money.",
    cta: { label: "Subscribe", to: "/login" },
    features: [
      { included: true, label: "50 documents / month" },
      { included: true, label: "Everything in Free" },
      { included: true, label: "Files up to 10 MB" },
      { included: true, label: "Download as JSON and CSV" },
      { included: true, label: "30-day document history" },
      { included: true, label: "REST API (10 req/min)" },
      { included: false, label: "Webhooks" },
      { included: false, label: "Custom schemas" },
    ],
  },
  {
    name: "Pro",
    plan: "pro",
    price: "$29",
    pricePeriod: "/ month",
    description: "Power users + developers.",
    cta: { label: "Subscribe", to: "/login" },
    highlight: true,
    features: [
      { included: true, label: "200 documents / month" },
      { included: true, label: "Everything in Starter" },
      { included: true, label: "Files up to 25 MB" },
      { included: true, label: "90-day document history" },
      { included: true, label: "REST API (60 req/min)" },
      { included: true, label: "Webhooks for async results" },
      { included: true, label: "Batch upload (up to 100 files)" },
      { included: true, label: "Priority email support" },
    ],
  },
  {
    name: "Business",
    plan: "business",
    price: "$99",
    pricePeriod: "/ month",
    description: "Teams and high volume.",
    cta: { label: "Subscribe", to: "/login" },
    features: [
      { included: true, label: "1,000 documents / month" },
      { included: true, label: "Everything in Pro" },
      { included: true, label: "Files up to 50 MB" },
      { included: true, label: "1-year document history" },
      { included: true, label: "REST API (300 req/min)" },
      { included: true, label: "Custom schemas" },
      { included: true, label: "Team accounts (up to 5 users)" },
      { included: true, label: "99.5% uptime SLA + DPA available" },
    ],
  },
];

export function PricingPage() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 md:py-20 space-y-12">
      <div className="text-center space-y-3 max-w-2xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Simple, predictable pricing.</h1>
        <p className="text-lg text-neutral-600">
          No "contact sales" gates until you actually need them. Start free, upgrade when you outgrow it.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {TIERS.map((tier) => (
          <TierCard key={tier.name} tier={tier} />
        ))}
      </div>

      {/* Credit packs — top-ups that work on any plan, including Free */}
      <section className="space-y-4 max-w-5xl mx-auto">
        <div className="text-center space-y-1">
          <h2 className="text-2xl font-bold">Or buy credits, no subscription needed</h2>
          <p className="text-neutral-600 text-sm">
            Credits roll over and never expire. Use them when your plan quota runs out,
            or instead of a subscription if your usage is bursty.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(Object.keys(CREDIT_PACKS) as CreditPack[]).map((id) => (
            <CreditPackCard key={id} id={id} />
          ))}
        </div>
        <p className="text-center text-xs text-neutral-500">
          One-time payment · Credits work across all plans · Stack with your subscription quota
        </p>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-6 md:p-8 space-y-4 max-w-3xl mx-auto">
        <h3 className="font-semibold text-lg">Frequently asked</h3>
        <FAQ q="What's the AI under the hood?">
          Anthropic Claude (vision-capable Sonnet model). Industry-leading vision quality, with the
          extraction prompts tuned by Trijazel's senior-dev review methodology.
        </FAQ>
        <FAQ q="Do you train on my documents?">
          No. Files are sent to Anthropic for inference and discarded. Anthropic does not train on
          API traffic by default. See <Link to="/legal/privacy" className="underline">Privacy</Link>.
        </FAQ>
        <FAQ q="How long are files retained?">
          Phase 1: files are processed and immediately discarded — nothing stored in DocLens beyond
          a usage record. Phase 2 (coming soon) adds document history with a 24-hour to 1-year
          window depending on plan.
        </FAQ>
        <FAQ q="Can I cancel anytime?">
          Yes. No commitments. Cancel from your dashboard and you keep access through the end of
          the billing period.
        </FAQ>
      </section>
    </div>
  );
}

function TierCard({ tier }: { tier: Tier }) {
  return (
    <div
      className={cn(
        "rounded-xl border bg-white p-6 space-y-4 flex flex-col",
        tier.highlight ? "border-brand ring-2 ring-brand/20 shadow-sm" : "border-neutral-200",
      )}
    >
      {tier.highlight && (
        <div className="text-xs font-medium text-brand uppercase tracking-wide">Most popular</div>
      )}
      <div>
        <h3 className="text-xl font-semibold">{tier.name}</h3>
        <p className="text-sm text-neutral-600 mt-1">{tier.description}</p>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold">{tier.price}</span>
        {tier.pricePeriod && <span className="text-neutral-500 text-sm">{tier.pricePeriod}</span>}
      </div>

      <ul className="space-y-2 text-sm flex-1">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            {f.included ? (
              <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            ) : (
              <X className="h-4 w-4 text-neutral-300 flex-shrink-0 mt-0.5" />
            )}
            <span className={f.included ? "text-neutral-700" : "text-neutral-400"}>{f.label}</span>
          </li>
        ))}
      </ul>

      {tier.plan ? (
        <UpgradeButton
          plan={tier.plan}
          variant={tier.highlight ? "primary" : "secondary"}
        >
          {tier.cta.label}
        </UpgradeButton>
      ) : (
        <Link
          to={tier.cta.to}
          className={cn(
            "block text-center rounded-lg px-4 py-2.5 font-medium transition",
            "border border-neutral-300 hover:bg-neutral-50",
          )}
        >
          {tier.cta.label}
        </Link>
      )}
    </div>
  );
}

function CreditPackCard({ id }: { id: CreditPack }) {
  const pack = CREDIT_PACKS[id];
  const isBestValue = id === "standard";
  return (
    <div
      className={cn(
        "rounded-xl border bg-white p-5 space-y-3 flex flex-col",
        isBestValue ? "border-brand ring-2 ring-brand/20" : "border-neutral-200",
      )}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-semibold text-lg capitalize">{id}</h3>
        {isBestValue && (
          <span className="text-xs font-medium text-brand uppercase tracking-wide">Best value</span>
        )}
      </div>
      <div className="space-y-0.5">
        <div className="text-3xl font-bold">{formatCurrency(pack.priceUsd)}</div>
        <div className="text-sm text-neutral-600">{pack.credits.toLocaleString()} documents</div>
        <div className="text-xs text-neutral-500">
          ≈ {formatCurrency(pack.perDoc)} per document
        </div>
      </div>
      <CreditPackButton pack={id} variant={isBestValue ? "primary" : "secondary"}>
        Buy {pack.credits} credits
      </CreditPackButton>
    </div>
  );
}

function FAQ({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="border-b border-neutral-200 last:border-0 pb-3">
      <summary className="font-medium cursor-pointer py-2 text-neutral-800 marker:text-neutral-400">
        {q}
      </summary>
      <p className="text-sm text-neutral-600 pt-1">{children}</p>
    </details>
  );
}
